import { Directive, Inject, InjectionToken, TemplateRef, ViewContainerRef } from '@angular/core';

/**
 * Who is looking at a report deck (NK-399).
 *
 * `internal` is a Navigator user with the deck's full chrome — editing, chat, history, the data
 * source switch. `client` is someone who opened a share link: the report, the filter rail, and
 * Present.
 */
export type ReportViewMode = 'client' | 'internal';

/**
 * **Defaults to `client`, and that is the whole point.**
 *
 * The deck renders for both audiences, so the risk is internal chrome reaching someone outside the
 * company. Defaulting to the restricted mode makes the failure safe in the direction that matters:
 * forget to provide it and chrome goes missing *in the editor*, where an internal user notices
 * within seconds and nothing has leaked. The opposite default fails silently, in a client's browser.
 *
 * The internal deck routes provide `internal` explicitly; the share-link viewer provides nothing.
 */
export const REPORT_VIEW_MODE = new InjectionToken<ReportViewMode>('REPORT_VIEW_MODE', {
  providedIn: 'root',
  factory: () => 'client',
});

/**
 * `*internalOnly` — renders its content only for a Navigator user, never through a share link.
 *
 * Reads the mode from DI rather than an input, so a new piece of chrome wraps itself in something
 * self-describing instead of remembering to thread a flag down from wherever it happens to sit.
 *
 * It is not the real guarantee, though: forgetting it still exposes something. The guarantee is the
 * allow-list test over the deck rendered in client mode, which fails when *anything* appears that
 * wasn't already expected. This directive exists to make doing the right thing obvious and cheap.
 */
@Directive({
  selector: '[internalOnly]',
  standalone: false,
})
export class InternalOnlyDirective {
  constructor(
    template: TemplateRef<unknown>,
    view: ViewContainerRef,
    @Inject(REPORT_VIEW_MODE) mode: ReportViewMode,
  ) {
    // Decided once at construction: the mode is fixed for the lifetime of a route -- a deck does not
    // become a share link while someone is looking at it -- so there is nothing to react to.
    if (mode === 'internal') view.createEmbeddedView(template);
  }
}
